import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import InnerLayout from "../../../components/layouts/inner-layout";
import { DataContext } from "../../../components/layouts/main-layout";
import { useHotkeys } from "../../../components/helper-components/use-hotkeys";
import AddButton from "../../../components/buttons/button";
import {
    getStoreListAction,
    getItemsListAction,
    addRegionalRestrictionAction,
    getCategoriesAction,
    getItemTypesAction,
    getItemGroupsAction,
    getManufacturersAction
} from "../../../redux/actions/regional-restrictions-action";
import errorToast from "../../../components/helper-components/error-toast";
import successToast from "../../../components/helper-components/success-toast";

const AddRegionalRestriction = () => {
    const { id } = useParams();
    const location = useLocation();
    const { permissionFn } = useContext(DataContext);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Edit mode data from navigation state
    const editData = location.state?.editData;
    const editViewMode = location.state?.viewMode;
    const [loading, setLoading] = useState(false);
    const [storeHierarchy, setStoreHierarchy] = useState({});
    const [selectedStores, setSelectedStores] = useState([]);
    const [expandedRegions, setExpandedRegions] = useState({});

    // Transaction restrictions - matching API format
    const [transactionRestrictions, setTransactionRestrictions] = useState({
        item_sales_invoice: false,
        item_sales_returns: false,
        item_purchase_request: false,
        item_discontinue: false,
        item_purchasing_order: false,
        item_credit_note: false,
        item_debit_note: false,
        item_stock_in: false,
        item_stock_out: false
    });

    // Product filters
    const [productFilters, setProductFilters] = useState({
        category: "",
        type: "",
        group: "",
        manufacturer: ""
    });

    // Dropdown options
    const [dropdownOptions, setDropdownOptions] = useState({
        categories: [],
        types: [],
        groups: [],
        manufacturers: []
    });

    // Products data
    const [products, setProducts] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const isEditMode = location.pathname.includes('/edit-regional-restriction');

    // Fetch data from APIs
    useEffect(() => {
        fetchStoreList();
        fetchDropdownOptions();
        fetchItemsList();
    }, []);

    // Handle save button positioning based on sidebar state
    useEffect(() => {
        const updateSaveButtonPosition = () => {
            const saveButton = document.getElementById('regional-restrictions-save-button');
            if (saveButton) {
                // Check if sidebar is expanded or collapsed by looking at main content container
                const mainContainer = document.querySelector('.main_layout_container, .main_container_not_exp');
                if (mainContainer) {
                    const containerRect = mainContainer.getBoundingClientRect();
                    const sidebarWidth = window.innerWidth - containerRect.width;
                    saveButton.style.left = `${sidebarWidth + 320}px`; // 320px for our internal sidebar
                    saveButton.style.right = '0px';
                }
            }
        };

        // Initial positioning
        updateSaveButtonPosition();

        // Listen for sidebar toggle changes
        const observer = new MutationObserver(updateSaveButtonPosition);
        const targetNode = document.querySelector('.main_layout_container, .main_container_not_exp');
        if (targetNode) {
            observer.observe(targetNode, { attributes: true, attributeFilter: ['class'] });
        }

        // Listen for window resize
        window.addEventListener('resize', updateSaveButtonPosition);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateSaveButtonPosition);
        };
    }, []);

    // Initial data loading
    useEffect(() => {
        fetchStoreList();
        fetchDropdownOptions();
        fetchItemsList();
    }, []);

    // Handle edit mode data population
    useEffect(() => {
        if (isEditMode && editData && products.length > 0) {
            if (editViewMode === "store") {
                // For store-based edit, populate selected stores and items
                if (editData.store_code) {
                    const storeData = {
                        id: editData.store_code,
                        name: editData.store_name,
                        code: editData.store_code,
                        fullName: editData.store_name
                    };
                    setSelectedStores([storeData]);
                }

                // Populate selected items from details by mapping item_code to item_id
                if (editData.details && editData.details.length > 0) {
                    const itemCodes = editData.details.map(detail => detail.item_code);
                    const matchingItemIds = products
                        .filter(product => itemCodes.includes(product.item_code))
                        .map(product => product.item_id);
                    setSelectedItems(matchingItemIds);
                }
            } else {
                // For item-based edit, populate selected items and stores
                if (editData.item_code) {
                    // Find the item_id from products list using item_code
                    const matchingProduct = products.find(product =>
                        product.item_code === editData.item_code || product.item_id === editData.item_id
                    );
                    if (matchingProduct) {
                        setSelectedItems([matchingProduct.item_id]);
                    }
                }

                // Populate selected stores from details
                if (editData.details && editData.details.length > 0) {
                    const storeList = editData.details.map(detail => ({
                        id: detail.store_code,
                        name: detail.store_name,
                        code: detail.store_code,
                        fullName: detail.store_name
                    }));
                    setSelectedStores(storeList);
                }
            }

            // Set a default or inferred transaction restrictions
            // This would ideally come from the API data
            setTransactionRestrictions({
                item_sales_invoice: true,
                item_sales_returns: false,
                item_purchase_request: true,
                item_discontinue: false,
                item_purchasing_order: true,
                item_credit_note: false,
                item_debit_note: true,
                item_stock_in: false,
                item_stock_out: true
            });
        }
    }, [isEditMode, editData, editViewMode, products]);

    const fetchStoreList = () => {
        dispatch(getStoreListAction(
            (response) => {
                if (response.status && response.data) {
                    setStoreHierarchy(transformStoreData(response.data));
                    // Auto-expand some regions by default
                    const initialExpanded = {};
                    response.data.regions?.forEach(region => {
                        initialExpanded[region.name] = true;
                        region.states?.forEach(state => {
                            initialExpanded[state.name] = true;
                        });
                    });
                    setExpandedRegions(initialExpanded);
                }
            },
            (error) => {
                console.error("Error fetching store list:", error);
                errorToast("Failed to fetch store list");
            }
        ));
    };

    const fetchDropdownOptions = () => {
        // Fetch categories
        dispatch(getCategoriesAction(
            (response) => {
                const categories = response?.data || response || [];
                setDropdownOptions(prev => ({
                    ...prev,
                    categories: categories.map(cat => ({
                        label: cat.name || cat.categoryName || cat,
                        value: cat.id || cat.categoryId || cat
                    }))
                }));
            },
            (error) => console.error("Error fetching categories:", error)
        ));

        // Fetch types
        dispatch(getItemTypesAction(
            (response) => {
                const types = response?.data || response || [];
                setDropdownOptions(prev => ({
                    ...prev,
                    types: types.map(type => ({
                        label: type.name || type.typeName || type,
                        value: type.id || type.typeId || type
                    }))
                }));
            },
            (error) => console.error("Error fetching types:", error)
        ));

        // Fetch groups
        dispatch(getItemGroupsAction(
            (response) => {
                const groups = response?.data || response || [];
                setDropdownOptions(prev => ({
                    ...prev,
                    groups: groups.map(group => ({
                        label: group.name || group.groupName || group,
                        value: group.id || group.groupId || group
                    }))
                }));
            },
            (error) => console.error("Error fetching groups:", error)
        ));

        // Fetch manufacturers
        dispatch(getManufacturersAction(
            (response) => {
                const manufacturers = response?.data || response || [];
                setDropdownOptions(prev => ({
                    ...prev,
                    manufacturers: manufacturers.map(mfr => ({
                        label: mfr.name || mfr.manufacturerName || mfr,
                        value: mfr.id || mfr.manufacturerId || mfr
                    }))
                }));
            },
            (error) => console.error("Error fetching manufacturers:", error)
        ));
    };

    const fetchItemsList = () => {
        // Build filter parameters
        const filterParams = {};
        if (productFilters.category) filterParams.item_category = productFilters.category;
        if (productFilters.type) filterParams.item_type = productFilters.type;
        if (productFilters.group) filterParams.item_group = productFilters.group;
        if (productFilters.manufacturer) filterParams.item_manufacturer = productFilters.manufacturer;

        dispatch(getItemsListAction(
            filterParams,
            (response) => {
                if (response.status && response.data) {
                    setProducts(response.data.map(item => ({
                        item_id: item.item_id,
                        itemCode: item.item_code,
                        itemName: item.item_name,
                        category: item.item_category || 'N/A',
                        type: item.item_type || 'N/A',
                        group: item.item_group || 'N/A',
                        manufacturer: item.item_manufacturer || 'N/A',
                        price: item.item_unit_price,
                        hsn: item.item_hsn,
                        isActive: item.is_active,
                        status: item.is_active ? "Active" : "Inactive"
                    })));
                }
            },
            (error) => {
                console.error("Error fetching items list:", error);
                errorToast("Failed to fetch items list");
            }
        ));
    };

    // Transform API data to component format
    const transformStoreData = (data) => {
        const transformed = {};

        data.regions?.forEach(region => {
            transformed[region.name] = {};

            region.states?.forEach(state => {
                transformed[region.name][state.name] = {};

                state.districts?.forEach(district => {
                    district.cities?.forEach(city => {
                        city.areas?.forEach(area => {
                            area.stores?.forEach(store => {
                                // Use area as the grouping level
                                if (!transformed[region.name][state.name][area.name]) {
                                    transformed[region.name][state.name][area.name] = {};
                                }
                                transformed[region.name][state.name][area.name][store.name] = {
                                    id: store.code,
                                    name: area.name,
                                    code: store.code,
                                    fullName: store.name
                                };
                            });
                        });
                    });
                });
            });
        });

        return transformed;
    };

    const toggleRegion = (region) => {
        setExpandedRegions(prev => ({
            ...prev,
            [region]: !prev[region]
        }));
    };

    const toggleStoreSelection = (store) => {
        setSelectedStores(prev => {
            const exists = prev.find(s => s.id === store.id);
            if (exists) {
                return prev.filter(s => s.id !== store.id);
            } else {
                return [...prev, store];
            }
        });
    };

    // Get all stores from a hierarchy node
    const getAllStoresFromNode = (node) => {
        let stores = [];

        const traverse = (obj) => {
            Object.entries(obj).forEach(([key, value]) => {
                if (typeof value === 'object' && value.id) {
                    // This is a store
                    stores.push(value);
                } else if (typeof value === 'object') {
                    // This is a container, traverse deeper
                    traverse(value);
                }
            });
        };

        traverse(node);
        return stores;
    };

    // Toggle selection for a parent node (region/state/area)
    const toggleParentSelection = (parentKey, parentNode, isChecked) => {
        const allStoresInParent = getAllStoresFromNode(parentNode);

        setSelectedStores(prev => {
            if (isChecked) {
                // Add all stores from this parent that aren't already selected
                const newStores = allStoresInParent.filter(store =>
                    !prev.find(s => s.id === store.id)
                );
                return [...prev, ...newStores];
            } else {
                // Remove all stores from this parent
                const storeIdsToRemove = allStoresInParent.map(store => store.id);
                return prev.filter(s => !storeIdsToRemove.includes(s.id));
            }
        });
    };

    // Check if a parent node is fully selected
    const isParentSelected = (parentNode) => {
        const allStoresInParent = getAllStoresFromNode(parentNode);
        if (allStoresInParent.length === 0) return false;

        return allStoresInParent.every(store =>
            selectedStores.find(s => s.id === store.id)
        );
    };

    // Check if a parent node is partially selected
    const isParentPartiallySelected = (parentNode) => {
        const allStoresInParent = getAllStoresFromNode(parentNode);
        if (allStoresInParent.length === 0) return false;

        const selectedCount = allStoresInParent.filter(store =>
            selectedStores.find(s => s.id === store.id)
        ).length;

        return selectedCount > 0 && selectedCount < allStoresInParent.length;
    };

    const removeSelectedStore = (storeId) => {
        setSelectedStores(prev => prev.filter(s => s.id !== storeId));
    };

    // Handle "Select All" button
    const handleSelectAll = () => {
        const allStores = getAllStoresFromNode(storeHierarchy);
        const areAllSelected = allStores.length > 0 && allStores.every(store =>
            selectedStores.find(s => s.id === store.id)
        );

        if (areAllSelected) {
            // Deselect all
            setSelectedStores([]);
        } else {
            // Select all
            setSelectedStores(allStores);
        }
    };

    const handleTransactionChange = (transaction) => {
        setTransactionRestrictions(prev => ({
            ...prev,
            [transaction]: !prev[transaction]
        }));
    };

    const handleFilterChange = (filterType, value) => {
        setProductFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const applyFilters = () => {
        console.log("Applying filters:", productFilters);
        fetchItemsList();
    };

    const clearFilters = () => {
        const clearedFilters = {
            category: "",
            type: "",
            group: "",
            manufacturer: ""
        };
        setProductFilters(clearedFilters);

        // Fetch items with cleared filters immediately
        const filterParams = {};
        if (clearedFilters.category) filterParams.item_category = clearedFilters.category;
        if (clearedFilters.type) filterParams.item_type = clearedFilters.type;
        if (clearedFilters.group) filterParams.item_group = clearedFilters.group;
        if (clearedFilters.manufacturer) filterParams.item_manufacturer = clearedFilters.manufacturer;

        dispatch(getItemsListAction(
            filterParams,
            (response) => {
                if (response.status && response.data) {
                    setProducts(response.data.map(item => ({
                        item_id: item.item_id,
                        itemCode: item.item_code,
                        itemName: item.item_name,
                        category: item.item_category || 'N/A',
                        type: item.item_type || 'N/A',
                        group: item.item_group || 'N/A',
                        manufacturer: item.item_manufacturer || 'N/A',
                        price: item.item_unit_price,
                        hsn: item.item_hsn,
                        isActive: item.is_active,
                        status: item.is_active ? "Active" : "Inactive"
                    })));
                }
            },
            (error) => {
                console.error("Error fetching items list:", error);
                errorToast("Failed to fetch items list");
            }
        ));
    };

    const handleSave = () => {
        if (selectedStores.length === 0) {
            errorToast("Please select at least one store");
            return;
        }

        if (selectedItems.length === 0) {
            errorToast("Please select at least one item");
            return;
        }

        const requestData = {
            store_id: selectedStores.map(store => store.id || store.code),
            item_id: selectedItems,
            restrictions: transactionRestrictions
        };

        setLoading(true);
        dispatch(addRegionalRestrictionAction(
            requestData,
            (response) => {
                setLoading(false);
                if (response.status) {
                    successToast("Regional restriction added successfully");
                    navigate("/regional-restrictions");
                } else {
                    errorToast(response.message || "Failed to add regional restriction");
                }
            },
            (error) => {
                setLoading(false);
                console.error("Error adding regional restriction:", error);
                errorToast("Failed to add regional restriction");
            }
        ));
    };

    const renderStoreHierarchy = (data, level = 0) => {
        return Object.entries(data).map(([key, value]) => {
            const isExpanded = expandedRegions[key];
            const hasChildren = typeof value === 'object' && !value.id;

            if (hasChildren) {
                const isParentFullySelected = isParentSelected(value);
                const isParentPartialSelected = isParentPartiallySelected(value);

                return (
                    <div key={key} style={{ marginLeft: `${level * 20}px` }}>
                        <div className="flex items-center py-1">
                            <svg
                                className={`w-4 h-4 mr-2 transition-transform text-[var(--description-color)] ${isExpanded ? 'rotate-90' : ''} cursor-pointer`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                onClick={() => toggleRegion(key)}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <div className="relative mr-2">
                                <input
                                    type="checkbox"
                                    className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                    checked={isParentFullySelected}
                                    onChange={(e) => toggleParentSelection(key, value, e.target.checked)}
                                />
                                {/* Indeterminate state indicator */}
                                {isParentPartialSelected && !isParentFullySelected && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="w-2 h-2 bg-[var(--primary-button-bg-color)] rounded-sm"></div>
                                    </div>
                                )}
                            </div>
                            <span
                                className="text-[length:var(--input-font-size)] text-[var(--input-color)] cursor-pointer"
                                onClick={() => toggleRegion(key)}
                            >
                                {key}
                            </span>
                        </div>
                        {isExpanded && renderStoreHierarchy(value, level + 1)}
                    </div>
                );
            } else {
                // This is a store
                const isSelected = selectedStores.some(s => s.id === value.id);
                return (
                    <div key={key} style={{ marginLeft: `${(level + 1) * 20}px` }}>
                        <div className="flex items-center py-1">
                            <div className="w-4 h-4 mr-2"></div>
                            <input
                                type="checkbox"
                                className="mr-2 rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                checked={isSelected}
                                onChange={() => toggleStoreSelection(value)}
                            />
                            <span
                                className="text-[length:var(--input-font-size)] text-[var(--input-color)] cursor-pointer"
                                onClick={() => toggleStoreSelection(value)}
                            >
                                {key}
                            </span>
                        </div>
                    </div>
                );
            }
        });
    };

    // Item selection functions
    const toggleItemSelection = (itemId) => {
        setSelectedItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    const selectAllItems = () => {
        const allItemIds = filteredProducts.map(product => product.item_id);
        const areAllSelected = allItemIds.every(id => selectedItems.includes(id));

        if (areAllSelected) {
            setSelectedItems(prev => prev.filter(id => !allItemIds.includes(id)));
        } else {
            setSelectedItems(prev => [...new Set([...prev, ...allItemIds])]);
        }
    };

    const filteredProducts = products.filter(product =>
        product.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.itemCode?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Hotkeys
    useHotkeys("ctrl+s", (e) => {
        e.preventDefault();
        handleSave();
    }, { enableOnFormTags: true });

    useHotkeys("escape", () => navigate("/regional-restrictions"));

    return (
        <InnerLayout onclkFn={"/inventory"} title={isEditMode ? "Edit Regional Restriction" : "Add Regional Restriction"} list={true}>
            <div className="flex h-screen bg-[var(--tertiary-color)]">
                {/* Store Hierarchy Sidebar */}
                <div className="w-80 bg-[var(--tertiary-color)] border-r border-[var(--layout-border-color)] flex flex-col">
                    <div className="p-4 border-b border-[var(--layout-border-color)]">
                        <h3 className="text-[length:var(--top-nav-heading-size)] font-[var(--top-nav-heading-weight)] text-[var(--top-nav-heading-color)] mb-4">Store Hierarchy</h3>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search Stores..."
                                className="w-full pl-8 pr-4 py-2 border border-[var(--input-border)] rounded-md text-[length:var(--input-font-size)] placeholder:text-[var(--input-place-color)] bg-[var(--input-bg)] text-[var(--input-color)] focus:outline-none focus:border-[var(--input-focus)]"
                            />
                            <svg className="absolute left-2 top-2.5 w-4 h-4 text-[var(--input-place-color)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] uppercase tracking-wider">REGIONS</span>
                                <button
                                    className="text-[length:var(--input-label-font-size)] text-[var(--primary-button-bg-color)] hover:text-[var(--primary-button-hover-color)]"
                                    onClick={handleSelectAll}
                                >
                                    {(() => {
                                        const allStores = getAllStoresFromNode(storeHierarchy);
                                        const areAllSelected = allStores.length > 0 && allStores.every(store =>
                                            selectedStores.find(s => s.id === store.id)
                                        );
                                        return areAllSelected ? 'Deselect All' : 'Select All';
                                    })()}
                                </button>
                            </div>
                            {renderStoreHierarchy(storeHierarchy)}
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-y-auto pb-40">
                    <div className="p-6">
                        {/* Selected Stores */}
                        <div className="bg-[var(--tertiary-color)] rounded-lg border border-[var(--layout-border-color)] mb-6">
                            <div className="p-6 border-b border-[var(--layout-border-color)]">
                                <h3 className="text-[length:var(--top-nav-heading-size)] font-[var(--top-nav-heading-weight)] text-[var(--top-nav-heading-color)] mb-1">
                                    Selected Stores ({selectedStores.length})
                                </h3>
                                <p className="text-[length:var(--top-nav-text-size)] text-[var(--top-nav-text-color)]">
                                    Manage product restrictions for the selected stores
                                </p>
                            </div>
                            <div className="p-6">
                                {selectedStores.length === 0 ? (
                                    <div className="text-center py-8">
                                        <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--list-table-heading-bg)] rounded-full mb-4">
                                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h2M7 3v18M15 3v18M9 7h6m-6 4h6m-6 4h6" />
                                            </svg>
                                        </div>
                                        <h4 className="text-[length:var(--top-nav-heading-size)] font-[var(--top-nav-heading-weight)] text-[var(--top-nav-heading-color)] mb-2">
                                            No Stores Selected
                                        </h4>
                                        <p className="text-[length:var(--top-nav-text-size)] text-[var(--description-color)] max-w-sm mx-auto">
                                            Select stores from the sidebar to manage product restrictions for those locations.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-3">
                                        {selectedStores.map((store) => (
                                            <div key={store.id} className="flex items-center bg-[var(--status-badge-blue-bg)] text-[var(--status-badge-blue-fg)] border border-[var(--status-badge-blue-border)] px-3 py-2 rounded-md text-[length:var(--status-badge-fs)] font-[var(--status-badge-fw)]">
                                                <span>Store {store.id} - {store.name}</span>
                                                <button
                                                    onClick={() => removeSelectedStore(store.id)}
                                                    className="ml-2 text-[var(--status-badge-blue-fg)] hover:opacity-75"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Transaction Restrictions */}
                        <div className="bg-[var(--tertiary-color)] rounded-lg border border-[var(--layout-border-color)] mb-6">
                            <div className="p-4 border-b border-[var(--layout-border-color)]">
                                <h3 className="text-[length:var(--top-nav-heading-size)] font-[var(--top-nav-heading-weight)] text-[var(--top-nav-heading-color)] mb-1">Transactions Restrictions</h3>
                                <p className="text-[length:var(--top-nav-text-size)] text-[var(--top-nav-text-color)]">Choose Transactions Restrictions</p>
                            </div>
                            <div className="p-4">
                                <div className="grid grid-cols-5 gap-4">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_sales_invoice}
                                            onChange={() => handleTransactionChange('item_sales_invoice')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Sales Invoice</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_sales_returns}
                                            onChange={() => handleTransactionChange('item_sales_returns')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Sales Returns</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_purchase_request}
                                            onChange={() => handleTransactionChange('item_purchase_request')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Purchase Request</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_discontinue}
                                            onChange={() => handleTransactionChange('item_discontinue')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Discontinue</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_purchasing_order}
                                            onChange={() => handleTransactionChange('item_purchasing_order')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Purchase Order</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_credit_note}
                                            onChange={() => handleTransactionChange('item_credit_note')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Credit Note</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_debit_note}
                                            onChange={() => handleTransactionChange('item_debit_note')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Debit Note</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_stock_in}
                                            onChange={() => handleTransactionChange('item_stock_in')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Stock In</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={transactionRestrictions.item_stock_out}
                                            onChange={() => handleTransactionChange('item_stock_out')}
                                            className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                        />
                                        <span className="text-[length:var(--input-font-size)] text-[var(--input-color)]">Stock Out</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Product Filters */}
                        <div className="bg-[var(--tertiary-color)] rounded-lg border border-[var(--layout-border-color)] mb-6">
                            <div className="p-4 border-b border-[var(--layout-border-color)]">
                                <h3 className="text-[length:var(--top-nav-heading-size)] font-[var(--top-nav-heading-weight)] text-[var(--top-nav-heading-color)] mb-1">Product Filters</h3>
                                <p className="text-[length:var(--top-nav-text-size)] text-[var(--top-nav-text-color)]">Filter products by category, type, group, or manufacturer</p>
                            </div>
                            <div className="p-4">
                                <div className="grid grid-cols-4 gap-4 mb-4">
                                    <div>
                                        <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-2">Item Categories</label>
                                        <select
                                            value={productFilters.category}
                                            onChange={(e) => handleFilterChange('category', e.target.value)}
                                            className="w-full border border-[var(--input-border)] rounded-md px-3 py-2 text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)] focus:outline-none focus:border-[var(--input-focus)]"
                                        >
                                            <option value="">All Categories</option>
                                            {dropdownOptions.categories.map((cat) => (
                                                <option key={cat.value} value={cat.value}>
                                                    {cat.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-2">Item Type</label>
                                        <select
                                            value={productFilters.type}
                                            onChange={(e) => handleFilterChange('type', e.target.value)}
                                            className="w-full border border-[var(--input-border)] rounded-md px-3 py-2 text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)] focus:outline-none focus:border-[var(--input-focus)]"
                                        >
                                            <option value="">All Types</option>
                                            {dropdownOptions.types.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-2">Item Group</label>
                                        <select
                                            value={productFilters.group}
                                            onChange={(e) => handleFilterChange('group', e.target.value)}
                                            className="w-full border border-[var(--input-border)] rounded-md px-3 py-2 text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)] focus:outline-none focus:border-[var(--input-focus)]"
                                        >
                                            <option value="">All Groups</option>
                                            {dropdownOptions.groups.map((group) => (
                                                <option key={group.value} value={group.value}>
                                                    {group.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] text-[var(--input-label-color)] mb-2">Manufacturer</label>
                                        <select
                                            value={productFilters.manufacturer}
                                            onChange={(e) => handleFilterChange('manufacturer', e.target.value)}
                                            className="w-full border border-[var(--input-border)] rounded-md px-3 py-2 text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)] focus:outline-none focus:border-[var(--input-focus)]"
                                        >
                                            <option value="">All Manufacturers</option>
                                            {dropdownOptions.manufacturers.map((mfr) => (
                                                <option key={mfr.value} value={mfr.value}>
                                                    {mfr.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={clearFilters}
                                        className="px-4 py-2 bg-[var(--header-button-bg-color)] border border-[var(--header-button-border-color)] text-[var(--header-button-fg-color)] text-[length:var(--primary-button-font-size)] font-[var(--primary-button-font-weight)] rounded-md hover:bg-[var(--header-button-hover-color)]"
                                    >
                                        Clear Filters
                                    </button>
                                    <button
                                        onClick={applyFilters}
                                        className="px-4 py-2 bg-[var(--status-badge-blue-bg)] text-[var(--status-badge-blue-fg)] text-[length:var(--primary-button-font-size)] font-[var(--primary-button-font-weight)] rounded-md hover:opacity-75"
                                    >
                                        Apply Filters
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Products Table */}
                        <div className="bg-[var(--tertiary-color)] rounded-lg border border-[var(--layout-border-color)]">
                            <div className="p-4 border-b border-[var(--layout-border-color)]">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-[length:var(--top-nav-heading-size)] font-[var(--top-nav-heading-weight)] text-[var(--top-nav-heading-color)]">Products ({products.length} items) - {selectedItems.length} selected</h3>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search Items..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-8 pr-4 py-2 border border-[var(--input-border)] rounded-md text-[length:var(--input-font-size)] bg-[var(--input-bg)] text-[var(--input-color)] placeholder:text-[var(--input-place-color)] focus:outline-none focus:border-[var(--input-focus)]"
                                        />
                                        <svg className="absolute left-2 top-2.5 w-4 h-4 text-[var(--input-place-color)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-[var(--list-table-border-color)]">
                                    <thead className="bg-[var(--list-table-heading-bg)]">
                                        <tr>
                                            <th className="px-6 py-3 text-center text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider w-16">
                                                <input
                                                    type="checkbox"
                                                    checked={filteredProducts.length > 0 && filteredProducts.every(product => selectedItems.includes(product.item_id))}
                                                    onChange={selectAllItems}
                                                    className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                                />
                                            </th>
                                            <th className="px-6 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">Item Code</th>
                                            <th className="px-6 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">Item Name</th>
                                            <th className="px-6 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">Category</th>
                                            <th className="px-6 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">Price</th>
                                            <th className="px-6 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">HSN</th>
                                            <th className="px-6 py-3 text-left text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] text-[var(--list-table-heading-fg-color)] uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-[var(--list-table-row-bg)] divide-y divide-[var(--list-table-border-color)]">
                                        {filteredProducts.map((product) => (
                                            <tr key={product.item_id} className="hover:bg-[var(--row-hover-color)]">
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-[length:var(--list-table-row-font-size)] text-[var(--list-table-row-fg-color)]">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedItems.includes(product.item_id)}
                                                        onChange={() => toggleItemSelection(product.item_id)}
                                                        className="rounded border-[var(--input-border)] text-[var(--primary-button-bg-color)]"
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-[length:var(--list-table-row-font-size)] text-[var(--list-table-row-fg-color)]">{product.itemCode}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-[length:var(--list-table-row-font-size)] text-[var(--list-table-row-fg-color)]">{product.itemName}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-[length:var(--list-table-row-font-size)] text-[var(--list-table-row-fg-color)]">{product.category}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-[length:var(--list-table-row-font-size)] text-[var(--list-table-row-fg-color)]">₹{product.price}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-[length:var(--list-table-row-font-size)] text-[var(--list-table-row-fg-color)]">{product.hsn}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-[length:var(--list-table-row-font-size)] text-[var(--list-table-row-fg-color)]">
                                                    <span className={`px-2 py-1 text-[length:var(--status-badge-fs)] rounded font-[var(--status-badge-fw)] ${product.isActive ? 'bg-[var(--status-badge-green-bg)] text-[var(--status-badge-green-fg)]' : 'bg-[var(--status-badge-red-bg)] text-[var(--status-badge-red-fg)]'
                                                        }`}>
                                                        {product.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="bg-[var(--list-pagination-bg)] px-4 py-3 flex items-center justify-between border-t border-[var(--list-table-border-color)]">
                                <div className="flex items-center">
                                    <span className="text-[length:var(--list-pagination-font-size)] text-[var(--list-pagination-text)]">
                                        Showing <span className="font-[var(--list-pagination-font-weight)]">{Math.min(filteredProducts.length, itemsPerPage)}</span> of{" "}
                                        <span className="font-[var(--list-pagination-font-weight)]">{filteredProducts.length}</span> [Items]
                                    </span>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                            className="border border-[var(--list-pagination-select-border)] bg-[var(--list-pagination-select-bg)] text-[var(--list-pagination-select-fg)] rounded-md px-3 py-1 text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)]"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </select>
                                        <span className="text-[length:var(--list-pagination-font-size)] text-[var(--list-pagination-text)]">rows / page</span>
                                    </div>
                                    <div className="flex space-x-1">
                                        <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-[var(--list-pagination-select-border)] bg-[var(--list-pagination-button-bg)] text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)] text-[var(--list-pagination-text)] hover:bg-[var(--list-pagination-button-hover-bg)]">
                                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                        <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-[var(--list-pagination-select-border)] bg-[var(--list-pagination-button-bg)] text-[length:var(--list-pagination-font-size)] font-[var(--list-pagination-font-weight)] text-[var(--list-pagination-text)] hover:bg-[var(--list-pagination-button-hover-bg)]">
                                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Save Button - Fixed to bottom, aligned with main content */}
            <div
                style={{ bottom: "0" }}
                className="fixed z-50 flex justify-between bg-white py-4 px-6 border-t border-[var(--layout-border-color)] shadow-lg"
                id="regional-restrictions-save-button"
            >
                <AddButton
                    prefixIcon={false}
                    isDisabled={loading}
                    onclkFn={() => navigate("/regional-restrictions")}
                    text={<span>Cancel</span>}
                    className="
                        flex items-center px-3 py-2 whitespace-nowrap rounded-md gap-[8px]
                        cursor-pointer
                        text-[var(--primary-button-bg-color)]
                        border border-[var(--primary-button-bg-color)]
                        text-[length:var(--primary-button-create-fs)]
                        font-[var(--primary-button-font-weight)]
                    "
                    type="button"
                />
                <AddButton
                    prefixIcon={false}
                    isDisabled={loading || selectedStores.length === 0 || selectedItems.length === 0}
                    loading={loading}
                    onclkFn={handleSave}
                    text={<span>{isEditMode ? "Update" : "Save"} <span className="opacity-50">(Ctrl+S)</span></span>}
                    className="
                        flex items-center px-3 py-2 whitespace-nowrap rounded-md gap-[8px]
                        bg-[var(--primary-button-bg-color)] cursor-pointer
                        text-[var(--primary-button-fg-color)]
                        border border-[var(--primary-button-bg-color)]
                        hover:bg-[var(--primary-button-hover-color)]
                        text-[length:var(--primary-button-create-fs)]
                        font-[var(--primary-button-font-weight)]
                    "
                    type="button"
                />
            </div>
        </InnerLayout>
    );
};

export default AddRegionalRestriction;
